import {RegFormKeysBasedOnAccountT, publicSpeculatorUpdateSchemaSchema} from '@tnc/shared/schemas';
import {useMemo} from 'react';
import {FormProvider} from 'react-hook-form';

import {FormStepT} from '@/lib/types/form.types';

import {FormIds} from '@/lib/forms/form-ids';
import {speculatorFormFields, speculatorFormSteps} from '@/lib/forms/speculator-form-config';

import {useAuth} from '@/providers/components/authProvider';
import {useAppForm} from '@/providers/components/formProvider';
import {useNotifications} from '@/providers/components/notificationsProvider';

import FormFields from '@/components/form/FormFields';
import FormGlobalError from '@/components/form/FormGlobalError';
import FormStepsButtons from '@/components/form/FormStepsButtons';
import FormSubmitButton from '@/components/form/FormSubmitButton';

function UserDataForm() {
  const {toast} = useNotifications();
  const {
    handleUpdateSpeculator,
    userAccounts: {speculator},
  } = useAuth();

  const defaultValues = useMemo(
    () => ({
      ...speculator,
      speculator_dob: speculator?.speculator_dob
        ? new Date(speculator.speculator_dob).toISOString().split('T')[0]
        : undefined,
      date_modified: new Date().toISOString(),
    }),
    [speculator]
  );

  // Create stable form config reference
  const _formConfig = {
    // Fields
    fields: speculatorFormFields,

    // Build field steps
    steps: speculatorFormSteps,

    // Default values
    defaultValues,

    // Schema
    zodSchema: publicSpeculatorUpdateSchemaSchema,
  };

  type UserDataFormKeysT = RegFormKeysBasedOnAccountT<'speculator'>;

  // Initialize form with stable config
  const {formConfig, setGlobalError, setLoading, goToStep} = useAppForm<UserDataFormKeysT>({
    formId: FormIds.USER_DATA,
    config: {
      // Fields
      fields: speculatorFormFields,

      // Build field steps
      steps: speculatorFormSteps,

      // Default values
      defaultValues,

      // Schema
      zodSchema: publicSpeculatorUpdateSchemaSchema,
    },
  });

  const {handleSubmit, trigger, setError, reset} = formConfig;

  // Helper function to find the step containing a field
  const findStepWithField = (fieldName: string, steps: FormStepT<string>) => {
    return steps.findIndex(step => step.fields.includes(fieldName));
  };

  // Submit handler
  const onSubmit = async (data: UserDataFormKeysT) => {
    try {
      setLoading(true);

      // Trigger validation for all fields
      const isValid = await trigger(undefined, {shouldFocus: true});

      if (!isValid) {
        window.scrollTo(0, 0);
        setGlobalError('Please check the form for errors and try again.');
        return;
      }

      // Validate with zod schema
      const validationResult = _formConfig.zodSchema.safeParse(data);

      if (!validationResult.success) {
        window.scrollTo(0, 0);
        setGlobalError('Please fix the validation errors below.');

        // Find the first field with an error and its corresponding step
        const firstErrorField = validationResult.error.issues[0]?.path.join('.');
        if (firstErrorField && _formConfig.steps) {
          const stepIndex = findStepWithField(firstErrorField, _formConfig.steps);
          if (stepIndex !== -1) {
            goToStep(stepIndex);
          }
        }

        validationResult.error.issues.forEach(issue => {
          const path = issue.path.join('.');
          setError(path as keyof UserDataFormKeysT, {
            type: 'validation',
            message: issue.message,
          });
        });

        return;
      }

      // Update the user
      const updatedSpeculator = await handleUpdateSpeculator(validationResult.data);

      // All done!
      toast.success({
        title: 'Update Complete!',
        message: 'You have been updated successfully.',
      });

      // Reset form with the updated speculator data returned from the API
      const updatedValues = {
        ...updatedSpeculator,
        speculator_dob: updatedSpeculator?.speculator_dob
          ? new Date(updatedSpeculator.speculator_dob).toISOString().split('T')[0]
          : undefined,
        date_modified: new Date().toISOString(),
      };

      reset(updatedValues);
      goToStep(0);
    } catch (error) {
      console.error({
        title: 'Update Error',
        message: (error as Error).message ?? 'Something went wrong. Please try again later.',
      });
      setGlobalError((error as Error).message ?? 'Something went wrong. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormProvider {...formConfig}>
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-6" noValidate>
        <FormGlobalError formId={FormIds.USER_DATA} />
        <FormStepsButtons formId={FormIds.USER_DATA} />
        <FormFields formId={FormIds.USER_DATA} />
        <FormSubmitButton formId={FormIds.USER_DATA} />
      </form>
    </FormProvider>
  );
}

export default UserDataForm;
